import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import AutoSizer from "react-virtualized-auto-sizer";
import { Album as AlbumType, Event } from "../types";
import LightBox from "./LightBox";
import { ReactWindowPhotoGrid } from "../../../EventGalleryMobile/PhotoGrid";
import {
  Photo,
  ThumbnailSelectionMode
} from "../../../EventGalleryMobile/types";
import Telemetry from "../../../../utils/rudderstackAnalytics";
import ahoy from "ahoy.js";
import { gssErrorLog } from "../../../../utils/gssLogger";
import "../../../EventGalleryMobile/styles/photos2.css";

const DESKTOP_THUMBNAIL_WIDTH = 140;
const DESKTOP_THUMBNAIL_HEIGHT = 140;

interface Photos2Props {
  photos: any[];
  cartHasPhotos: boolean;
  addToCart: (photo: any) => void;
  removeFromCart: (photo: any) => void;
  loadPhotos: () => void;
  hasMore: boolean;
  reset?: boolean;
  event: Event;
  album: AlbumType;
  eventClickable?: boolean;
  cart?: any;
  isAuthorizedForDirectDownloads?: boolean;
  primaryColor?: string;
  userId?: number;
  isEmbed?: boolean;
  trackTelemetry?: boolean;
  curationApiCalled?: boolean;
  setCurationApiCalled: (state: boolean) => void;
  parentName?: string;
  securedOrgView?: boolean;
  disableSelectIcon?: boolean;
  directDownloadEnabled?: boolean;
}

const Photos2: React.FC<Photos2Props> = props => {
  const [photoIndex, setPhotoIndex] = useState(-1);

  const {
    photos,
    addToCart,
    removeFromCart,
    hasMore,
    loadPhotos,
    event,
    album,
    primaryColor,
    trackTelemetry = true,
    eventClickable = false,
    cart,
    isAuthorizedForDirectDownloads = false,
    userId,
    isEmbed = false,
    curationApiCalled = false,
    setCurationApiCalled,
    parentName,
    securedOrgView = false,
    disableSelectIcon = false,
    directDownloadEnabled = false
  } = props;

  const convertedPhotos: Photo[] = useMemo(
    () =>
      photos.map(photo => ({
        ...photo,
        isSelected: cart?.photos?.includes(photo.id) || false,
        src: "",
        disableContextMenu: true
      })),
    [photos, cart?.photos]
  );

  const onClickThumbnail = (photo: Photo) => {
    const index = convertedPhotos.findIndex((p: Photo) => p.id === photo.id);
    if (index === -1) {
      console.error("Photos2: onClickThumbnail: photo not found", photo.id);
      return;
    }
    toggleLightBox(index);
  };

  const toggleLightBox = (index: number) => {
    try {
      if (
        convertedPhotos[photoIndex] &&
        convertedPhotos[photoIndex].videoContent
      ) {
        setPhotoIndex(-1);
      }
      setPhotoIndex(index);

      if (index >= 0 && convertedPhotos[index]) {
        if (trackTelemetry) {
          ahoy.track("albumInteraction", {
            secondary_type: "openedPhotoLightbox",
            album_id: album?.id,
            event_id: event?.id
          });
          Telemetry.track("album:opened-lightbox", {
            eventId: event?.id,
            eventName: event?.name,
            albumId: album?.id,
            photoId: convertedPhotos[index].id,
            embeddedMode: isEmbed
          });
        }
      }
    } catch (error) {
      gssErrorLog("Photos2: toggleLightBox: error", error);
      setPhotoIndex(-1);
    }
  };

  const updatePhotoInCart = (photo: Photo) => {
    const originalPhoto = photos.find(p => p.id === photo.id);
    if (!originalPhoto) return;

    if (photo.isSelected) {
      if (trackTelemetry) {
        Telemetry.track("album:remove-from-cart", {
          eventId: event?.id,
          eventName: event?.name,
          albumId: album?.id,
          photoId: photo.id,
          cartId: cart?.id,
          source: "thumbnail",
          embeddedMode: isEmbed
        });
      }
      removeFromCart(originalPhoto);
    } else {
      if (trackTelemetry) {
        Telemetry.track("album:add-to-cart", {
          eventId: event?.id,
          eventName: event?.name,
          albumId: album?.id,
          photoId: photo.id,
          cartId: cart?.id,
          source: "thumbnail",
          embeddedMode: isEmbed
        });
      }
      addToCart(originalPhoto);
    }
  };

  const onThumbnailSelect = (photo: Photo): void => {
    updatePhotoInCart(photo);
  };

  const areMoreItemsLoading = !hasMore
    ? false
    : convertedPhotos.length % 100 === 0;

  return (
    <div
      className="photos2-component photos2-desktop-browse-event"
      style={{ overflowX: "hidden" }}
    >
      <AutoSizer disableHeight onResize={() => {}}>
        {({ width }: { width: number }) => (
          <ReactWindowPhotoGrid
            items={convertedPhotos}
            hasMoreItems={hasMore}
            areMoreItemsLoading={areMoreItemsLoading}
            loadMoreItems={loadPhotos}
            numColumns={Math.max(
              1,
              Math.floor(width / DESKTOP_THUMBNAIL_WIDTH)
            )}
            width={width}
            otherProps={{
              event: event,
              forceRender: false,
              isMobile: false,
              thumbnailProps: {
                onClick: onClickThumbnail,
                onThumbnailSelect: onThumbnailSelect,
                selectionMode: ThumbnailSelectionMode.CART,
                primaryColor: primaryColor,
                securedOrgView: securedOrgView,
                disableSelectIcon: disableSelectIcon
              },
              onScrollDownStarted: () => {},
              thumbnailWidth: DESKTOP_THUMBNAIL_WIDTH,
              thumbnailHeight: DESKTOP_THUMBNAIL_HEIGHT
            }}
          />
        )}
      </AutoSizer>
      {convertedPhotos[photoIndex] && (
        <LightBox
          photos={convertedPhotos}
          photoIndex={photoIndex}
          showLightBox={toggleLightBox}
          closeLightBox={() => toggleLightBox(-1)}
          cartHasPhotos={cart?.photos?.length > 0}
          addToCart={addToCart}
          removeFromCart={removeFromCart}
          event={convertedPhotos[photoIndex]?.album?.event || event}
          album={convertedPhotos[photoIndex]?.album || album}
          eventClickable={eventClickable}
          cart={cart}
          isAuthorizedForDirectDownloads={isAuthorizedForDirectDownloads}
          primaryColor={primaryColor}
          userId={userId}
          isEmbed={isEmbed}
          photoSaved={false}
          curationApiCalled={curationApiCalled}
          setCurationApiCalled={setCurationApiCalled}
          parentName={parentName}
          directDownloadEnabled={directDownloadEnabled}
          securedGalleryView={securedOrgView ?? false}
        />
      )}
    </div>
  );
};

Photos2.propTypes = {
  photos: PropTypes.array.isRequired,
  cartHasPhotos: PropTypes.bool.isRequired,
  addToCart: PropTypes.func.isRequired,
  removeFromCart: PropTypes.func.isRequired,
  loadPhotos: PropTypes.func.isRequired,
  hasMore: PropTypes.bool.isRequired,
  reset: PropTypes.bool,
  event: PropTypes.object.isRequired,
  album: PropTypes.object.isRequired,
  eventClickable: PropTypes.bool,
  cart: PropTypes.object,
  isAuthorizedForDirectDownloads: PropTypes.bool,
  primaryColor: PropTypes.string,
  userId: PropTypes.number,
  isEmbed: PropTypes.bool,
  trackTelemetry: PropTypes.bool,
  curationApiCalled: PropTypes.bool,
  setCurationApiCalled: PropTypes.func.isRequired,
  parentName: PropTypes.string,
  securedOrgView: PropTypes.bool,
  disableSelectIcon: PropTypes.bool,
  directDownloadEnabled: PropTypes.bool
};

export default Photos2;
